import { AlertTriangle, Lightbulb, Loader2, MessageCircle, Pencil, RefreshCw, Sparkles, Trash2, TrendingUp } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { askAdvisor, fetchMarketLandscape, fetchPortfolio, fetchPortfolioBriefing, fetchRecommendations, fetchTrades, resetPortfolio, updatePortfolioCash } from '../api';
import GuideCard from './GuideCard';
import { Mode } from './Header';
import { gradeLabel, sigLabel } from '../interpret';   // 📖 화면 이름 사전(TERMS.md) — 값은 그대로, 글자만 바꿈
import RealizedTrades from './RealizedTrades';
import { AdvisorMessage, MarketLandscape, PortfolioBriefing, PortfolioPosition, PortfolioStatus, PortfolioTag, RecommendationCard, Recommendations, TradesResponse } from '../types';

// 🎨 규격 V2(08-23) 부품 — 버튼은 고스트/켜짐 두 가지, 칩은 중립/방향/경고. 색은 dc 토큰만(밝은 원색·보라 금지).
const BTN = 'inline-flex items-center gap-1.5 h-[26px] px-2 sm:h-[29px] sm:px-[11px] rounded-lg border text-[11.5px] sm:text-xs transition-colors';
const GHOST = `${BTN} border-[#202A3C] text-dc-ink2/90 font-medium hover:border-[#33405A] hover:text-dc-ink1 disabled:opacity-40 disabled:hover:border-[#202A3C]`;
const ON = `${BTN} border-dc-point/35 bg-dc-point/10 text-dc-point font-semibold disabled:opacity-40`;
// 파괴적 동작(초기화)만 예외로 상승색 테두리 허용 — 부품 시트 단서
const DANGER = `${BTN} border-dc-up/30 text-dc-up/80 hover:border-dc-up/60 hover:text-dc-up`;
const DANGER_ON = `${BTN} border-dc-up/45 bg-dc-up/10 text-dc-up font-semibold`;

const KICKER = 'text-[10.5px] font-[650] tracking-[.12em] text-dc-ink3';
const CHIP = 'inline-flex items-center h-[22px] px-2 rounded-md border text-[11.5px] font-medium whitespace-nowrap';
const MINICHIP = 'inline-flex items-center h-[18px] px-1.5 rounded-[5px] border text-[10.5px] font-medium whitespace-nowrap';
const TONE = {
  point:  'border-dc-point/35 bg-dc-point/[0.08] text-dc-point',
  up:     'border-dc-up/40 bg-dc-up/[0.08] text-dc-up',
  upSoft: 'border-dc-up/30 bg-dc-up/[0.06] text-dc-up',
  down:   'border-dc-down/35 bg-dc-down/[0.07] text-dc-down',
  warn:   'border-dc-warn/40 bg-dc-warn/[0.08] text-dc-warn',
  ok:     'border-dc-ok/35 bg-dc-ok/[0.06] text-dc-ok',
  strong: 'border-dc-line2 bg-dc-bg2 text-dc-ink1',
  mute:   'border-dc-line1 bg-dc-bg1 text-dc-ink2',
  faint:  'border-dc-line1 bg-dc-base/70 text-dc-ink3',
} as const;
const INPUT = 'bg-dc-bg1 border border-[#202A3C] rounded-lg px-2 text-xs text-dc-ink2 outline-none focus:border-dc-point/50';
// KPI는 카드 상자가 아니라 '선으로 나눈 구획' — 폰(2열)에선 3번째 칸이 첫 열이라 선을 뺀다.
const KPI_DIV = 'border-l border-dc-hair pl-5';
const KPI_DIV_LG = 'lg:border-l lg:border-dc-hair lg:pl-5';

// 📍 시장 한 줄 스트립 — 내 현황엔 맥락만, 자세히는 '시장' 탭으로 점프(개편으로 시장 분석은 메인탭 이사).
function MarketStrip({ onTab }: { onTab?: (m: Mode) => void }) {
  const [land, setLand] = useState<MarketLandscape | null>(null);
  useEffect(() => { fetchMarketLandscape().then(setLand).catch(() => {}); }, []);
  const p = land?.position;
  return (
    <button onClick={() => onTab?.('market')}
      className="w-full text-left rounded-lg border border-dc-line1 bg-dc-bg1/60 px-3 py-2 flex items-center gap-2 text-[11.5px] hover:border-dc-line2 transition-colors">
      <span className="text-dc-point shrink-0">📍 시장</span>
      {p ? (
        <span className="text-dc-ink2 min-w-0 truncate">
          코스피 {p.off_high_pct != null ? `고점 대비 ${p.off_high_pct}%` : p.kospi.toLocaleString('ko-KR')}
          {p.regime ? ` · ${p.regime} ${p.regime_days}일째` : ''}{p.down_days >= 1 ? ` · 하락 ${p.down_days}일` : ''}
        </span>
      ) : <span className="text-dc-ink3">불러오는 중…</span>}
      <span className="ml-auto text-dc-point/70 text-[10.5px] shrink-0">시장 분석 자세히 →</span>
    </button>
  );
}

interface Props {
  onSelectTicker: (ticker: string, name: string) => void;
  onTab?: (m: Mode) => void;
}

// 시그널 칩 — 강한 추세일수록 웜(상승색), 모양만 잡힌 쪽은 중립, 힘 빠진 쪽은 흐림·주의색(ScannerTable과 동일 규격).
const SIGNAL_STYLE: Record<string, string> = {
  신규진입: TONE.mute, 완벽한추세: TONE.up, 강한추세: TONE.upSoft,
  추세형성: TONE.mute, 단기추세: TONE.mute,
  관망: TONE.faint, 단기관망: TONE.faint, 위험: TONE.warn,
};

function won(v: number): string {
  return v.toLocaleString('ko-KR');
}

// 자산 추이 스파크라인 (recharts 없이 SVG — 가벼움). 색 관례는 앱 전체와 같다: 상승=웜(dc.up)·하락=쿨(dc.down).
function EquitySparkline({ data }: { data: { date: string; total_account: number }[] }) {
  const vals = data.map((d) => d.total_account);
  const min = Math.min(...vals), max = Math.max(...vals), range = max - min || 1;
  const W = 600, H = 60;
  const pts = vals.map((v, i) => `${((i / (vals.length - 1)) * W).toFixed(1)},${(H - ((v - min) / range) * H).toFixed(1)}`).join(' ');
  const first = vals[0], last = vals[vals.length - 1], up = last >= first;
  const won = (n: number) => n.toLocaleString('ko-KR');
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14" preserveAspectRatio="none">
        <polyline points={pts} fill="none" stroke={up ? '#E06C5B' : '#5C8FD6'} strokeWidth="2" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="flex justify-between text-[10.5px] text-dc-ink3 mt-1">
        <span>{data[0].date.slice(5)} · {won(first)}원</span>
        <span className={up ? 'text-dc-up' : 'text-dc-down'}>
          {data[data.length - 1].date.slice(5)} · {won(last)}원 ({up ? '+' : ''}{won(last - first)})
        </span>
      </div>
    </div>
  );
}

// KPI 한 칸 — 상자 대신 헤어라인 구획. 라벨=키커 · 숫자=num-lg(주인공만 num-xl) · 단위는 55% 크기·85% 불투명.
function StatCell({ label, value, unit, sub, color, hero, divider }: {
  label: string; value: string; unit?: string; sub?: string; color?: string; hero?: boolean; divider?: string;
}) {
  return (
    <div className={`flex flex-col gap-1 ${divider ?? ''}`}>
      <p className={KICKER}>{label}</p>
      {/* 폰(375) 2열에선 사다리 한 칸 아래로 — 억 단위 금액이 칸을 넘거나 단위(원)만 다음 줄로 떨어지던 것 차단 */}
      <p className={`${hero ? 'text-[24px] sm:text-[27px] font-[760] tracking-[-.015em]' : 'text-[17.5px] sm:text-[24px] font-[750]'} tabular-nums leading-none whitespace-nowrap ${color ?? 'text-dc-ink1'}`}>
        {value}{unit && <span className="text-[13px] opacity-85">{unit}</span>}
      </p>
      {sub && <p className="text-[11.5px] text-dc-ink3">{sub}</p>}
    </div>
  );
}

function CashCell({ cash, cashPct, onSave, divider }: { cash: number; cashPct: number; onSave: (v: number) => void; divider?: string }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(cash || ''));

  if (editing) {
    return (
      <div className={`flex flex-col gap-1 ${divider ?? ''}`}>
        <p className={KICKER}>추가 매수 여력 (현금)</p>
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            autoFocus
            className={`w-full h-[29px] ${INPUT}`}
          />
          <button
            onClick={() => { onSave(Number(val) || 0); setEditing(false); }}
            className={`${ON} shrink-0`}
          >
            저장
          </button>
        </div>
      </div>
    );
  }
  return (
    <button
      onClick={() => setEditing(true)}
      className={`flex flex-col gap-1 text-left group ${divider ?? ''}`}
      title="클릭하여 현금 수정"
    >
      <p className={`${KICKER} flex items-center gap-1`}>
        추가 매수 여력 <Pencil size={9} className="opacity-0 group-hover:opacity-100" />
      </p>
      <p className="text-[17.5px] sm:text-[24px] font-[750] tabular-nums leading-none whitespace-nowrap text-dc-ink1 group-hover:text-dc-point transition-colors">
        {cash.toLocaleString('ko-KR')}<span className="text-[13px] opacity-85">원</span>
      </p>
      <p className="text-[11.5px] text-dc-ink3">현금비중 {cashPct}%</p>
    </button>
  );
}

function AllocationBar({ invested, cash }: { invested: number; cash: number }) {
  return (
    <div>
      <div className="flex justify-between text-[11.5px] text-dc-ink3 mb-1.5">
        <span>주식 <b className="text-dc-ink1 tabular-nums">{invested}%</b></span>
        <span>현금 <b className="text-dc-ink1 tabular-nums">{cash}%</b></span>
      </div>
      <div className="flex h-1 rounded-full overflow-hidden bg-dc-hair">
        <div className="bg-dc-point h-full" style={{ width: `${invested}%` }} />
        <div className="bg-dc-ink3/60 h-full" style={{ width: `${cash}%` }} />
      </div>
    </div>
  );
}

function TagRow({ label, tags, tone, onClick }: {
  label: string; tags: PortfolioTag[]; tone: string; onClick: (t: string, n: string) => void;
}) {
  if (!tags.length) return null;
  return (
    <div className="flex items-start gap-2">
      <span className={`text-[11.5px] font-medium shrink-0 mt-0.5 ${tone}`}>{label}</span>
      <div className="flex flex-wrap gap-1">
        {tags.map((t) => (
          <button
            key={t.ticker}
            onClick={() => onClick(t.ticker, t.name)}
            className={`${CHIP} ${TONE.mute} hover:border-dc-line2 hover:text-dc-ink1 transition-colors`}
          >
            {t.name}
          </button>
        ))}
      </div>
    </div>
  );
}

const DIV_STYLE: Record<string, string> = {
  '분산 큼': TONE.ok,          // 내 보유와 안 겹침 = 조건 통과(초록은 여기만)
  '보통': TONE.mute,
  '유사(쏠림)': TONE.warn,
  '─': TONE.faint,
};

const TIMING_STYLE: Record<string, string> = {
  '진입적기': TONE.upSoft,     // 매수하기 좋은 때 = 웜
  '관찰': TONE.mute,
  '추격주의': TONE.warn,
};

function RecCard({ c, onClick }: { c: RecommendationCard; onClick: (t: string, n: string) => void }) {
  const isRev = c.kind === 'reversion';
  const scoreText = isRev ? `역추세 ${c.reversion_score}` : `점수 ${c.score}`;
  const sig = isRev ? c.reversion_signal : c.signal;
  const mom = c.momentum_6m;
  const chg = c.price_change_pct;
  return (
    <button
      onClick={() => onClick(c.ticker, c.name)}
      className="text-left rounded-lg border border-dc-line1 bg-dc-bg1/60 px-3 py-2.5 hover:border-dc-line2 transition-colors"
    >
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-dc-ink1 text-[13px] font-bold truncate min-w-0">{c.name}</span>
        {/* 진입 타이밍 배지 — 추격주의면 경고색(이미 많이 오른 추격주 경고) */}
        {c.timing_label && (
          <span className={`shrink-0 ${MINICHIP} ${TIMING_STYLE[c.timing_label] ?? TIMING_STYLE['관찰']}`}>
            {gradeLabel(c.timing_label)}
          </span>
        )}
        <span className={`shrink-0 ml-auto ${MINICHIP} ${DIV_STYLE[c.diversify] ?? DIV_STYLE['─']}`}>
          {c.diversify}{c.corr != null && ` ${c.corr}`}
        </span>
      </div>
      <div className="flex items-center gap-2 text-[11.5px] text-dc-ink3">
        <span className="text-dc-ink2">{scoreText}</span>
        {sig && <span>{sigLabel(sig)}</span>}
      </div>
      {/* 과열 맥락 — 6개월 모멘텀(이격) + 오늘 등락(추격 여부) */}
      {(mom != null || chg != null) && (
        <div className="text-[11.5px] text-dc-ink3 mt-0.5">
          {mom != null && (
            <>6개월 <b className={mom >= 100 ? 'text-dc-warn' : mom >= 60 ? 'text-dc-up' : 'text-dc-ink2'}>{mom >= 0 ? '+' : ''}{Math.round(mom)}%</b></>
          )}
          {chg != null && (
            <> · 오늘 <b className={chg >= 8 ? 'text-dc-warn' : 'text-dc-ink2'}>{chg >= 0 ? '+' : ''}{chg.toFixed(1)}%</b></>
          )}
        </div>
      )}
      <div className="text-[11.5px] text-dc-ink3 mt-0.5">
        {Number(c.current_price).toLocaleString('ko-KR')}원 · 현금으로 최대 <b className="text-dc-ink2">{c.max_shares}주</b>
      </div>
    </button>
  );
}

function AdvisorChat() {
  const [messages, setMessages] = useState<AdvisorMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  const send = async (q?: string) => {
    const question = (q ?? input).trim();
    if (!question || sending) return;
    const base = [...messages, { role: 'user' as const, text: question }];
    setMessages(base);
    setInput('');
    setSending(true);
    try {
      const r = await askAdvisor(question, messages);
      setMessages([...base, { role: 'assistant', text: r.answer }]);
    } catch {
      setMessages([...base, { role: 'assistant', text: 'AI 응답이 일시적으로 안 되네요. 잠시 후 다시 시도해 주세요.' }]);
    } finally {
      setSending(false);
    }
  };

  const suggestions = ['현금으로 뭘 사면 분산이 될까?', '내 쏠림 위험은 어때?', '지금 시장에서 조심할 건?'];

  return (
    <div className="rounded-lg border border-dc-line1 bg-dc-bg1/60 px-3 py-3">
      <p className="flex items-center gap-1.5 mb-2.5">
        <MessageCircle size={13} className="text-dc-point" />
        <span className={KICKER}>어드바이저에게 물어보기</span>
        <span className="text-[10.5px] text-dc-ink3">(내 데이터 기준)</span>
      </p>

      {messages.length > 0 && (
        <div className="space-y-2 mb-2 max-h-80 overflow-y-auto">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-[13px] leading-snug border ${
                m.role === 'user'
                  ? 'border-dc-point/25 bg-dc-point/[0.06] text-dc-ink1'
                  : 'border-dc-line1 bg-dc-bg0/70 text-dc-ink2'
              }`}>
                {m.text}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="rounded-lg border border-dc-line1 bg-dc-bg0/70 px-2.5 py-1.5">
                <Loader2 size={13} className="animate-spin text-dc-ink3" />
              </div>
            </div>
          )}
        </div>
      )}

      {messages.length === 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {suggestions.map((s) => (
            <button
              key={s}
              onClick={() => send(s)}
              disabled={sending}
              className={GHOST}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-1.5">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          placeholder="예: SK하이닉스 지금 어때?"
          disabled={sending}
          className={`flex-1 h-[29px] ${INPUT} placeholder-dc-ink3/70 disabled:opacity-50`}
        />
        <button
          onClick={() => send()}
          disabled={sending || !input.trim()}
          className={ON}
        >
          전송
        </button>
      </div>
      <p className="text-dc-ink3/80 text-[10.5px] mt-2">데이터 기반 분석 도우미일 뿐 면허 있는 투자상담이 아니며, 예측·매수매도 명령은 하지 않습니다. 최종 판단은 본인.</p>
    </div>
  );
}

export default function MyStatusPage({ onSelectTicker, onTab }: Props) {
  const [data, setData] = useState<PortfolioStatus | null>(null);
  const [brief, setBrief] = useState<PortfolioBriefing | null>(null);
  const [recs, setRecs] = useState<Recommendations | null>(null);
  const [recMarket, setRecMarket] = useState<'kr' | 'us'>('kr'); // 추천 한국/미국 토글
  const [loading, setLoading] = useState(false);
  const [loadingBrief, setLoadingBrief] = useState(false);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [error, setError] = useState('');
  const [advisorOpen, setAdvisorOpen] = useState(false);   // 어드바이저는 하단 접기(상태 유지)
  const [confirmReset, setConfirmReset] = useState(false); // 초기화 2단계 확인
  const [trades, setTrades] = useState<TradesResponse | null>(null);   // 실현 매매 (있을 때만 우측 컬럼)

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setBrief(null);
    setRecs(null);
    try {
      const p = await fetchPortfolio();
      setData(p);
      fetchTrades().then(setTrades).catch(() => setTrades(null));   // 실현 매매 (우측 컬럼 표시 여부 결정)
      if (p.has_holdings) {
        setLoadingBrief(true);
        fetchPortfolioBriefing()
          .then(setBrief)
          .catch(() => setBrief({ headline: '', diagnosis: 'AI 브리핑 일시 불가 — 아래 수치를 참고하세요.', market_position: '', positions_note: [], cash_strategy: '', actions: [] }))
          .finally(() => setLoadingBrief(false));
        setLoadingRecs(true);
        fetchRecommendations()
          .then(setRecs)
          .catch(() => setRecs(null))
          .finally(() => setLoadingRecs(false));
      }
    } catch {
      setError('데이터 로딩 실패. 백엔드 서버를 확인하세요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveCash = async (v: number) => {
    try { await updatePortfolioCash(v); load(); } catch { /* silent */ }
  };

  const doReset = async () => {
    setConfirmReset(false);
    try { await resetPortfolio(); load(); } catch { /* silent */ }
  };

  if (error) {
    return <div className="flex-1 p-6 text-dc-warn text-[13px]">{error}</div>;
  }

  if (!data) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 text-dc-ink2 text-[13px]">
        <Loader2 size={16} className="animate-spin" /> 내 현황 분석 중...
      </div>
    );
  }

  if (!data.has_holdings) {
    // 처음 유저(보유·현금 없음): 빈 화면 대신 개인정보 불필요한 '시장 분석'을 바로 보여주고
    // 보유·현금 입력을 부드럽게 유도. 시장분석엔 '진입 신호판'(전액 현금일 때 진입 타이밍)이 포함됨.
    return (
      <div className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto p-4 space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-dc-ink1 text-[14px] font-[750]">📊 내 현황</h2>
            <span className={`${CHIP} ${TONE.mute}`}>현금 대기</span>
            <button
              onClick={load}
              disabled={loading}
              className={`ml-auto ${GHOST}`}
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> 새로고침
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-4 items-center">
            <div className="sm:col-span-2 rounded-lg border border-dc-line1 bg-dc-bg1/60 px-3.5 py-3 flex flex-col justify-center">
              <p className="text-dc-ink1 text-[13px] font-bold mb-1.5">아직 내 정보가 비어 있어요</p>
              <p className="text-dc-ink3 text-[11.5px] leading-relaxed">
                · 보유 종목 → <b className="text-dc-ink2">관심종목 탭에서 ✏️ 보유 입력</b> (또는 🟢 매수)<br />
                · 현금 → 오른쪽 칸에 입력
                <br />넣으면 <b className="text-dc-ink2">내 포지션 기준 분석</b>이 더해져요. 그동안 <b className="text-dc-ink2">시장 탭</b>에서 오늘 장을 보세요 ↓
              </p>
            </div>
            <CashCell cash={data.summary.cash} cashPct={data.summary.cash_pct} onSave={saveCash} divider="sm:border-l sm:border-dc-hair sm:pl-5" />
          </div>

          {/* 📍 시장 맥락 — 자세히는 '시장' 탭(개편으로 시장 분석 이사) */}
          <MarketStrip onTab={onTab} />
        </div>
      </div>
    );
  }

  const s = data!.summary;
  const r = data!.regime;
  const pnlColor = s.total_pnl_pct > 0 ? 'text-dc-up' : s.total_pnl_pct < 0 ? 'text-dc-down' : 'text-dc-ink2';
  const overheat = (r.kospi_above_ma200_pct ?? 0) >= 30;
  const hasPositions = data!.positions.length > 0;   // 보유 중 vs 현금 대기 — 화면 구성 분기

  // 추천·실현은 두 상태에서 공통으로 쓰므로 변수로 한 번만 정의(중복 방지)
  const recsBlock = (loadingRecs || recs) && (
    <div className="border-t border-dc-hair pt-4">
      <p className="flex items-center gap-1.5 mb-1.5">
        <Lightbulb size={13} className="text-dc-point" />
        <span className={KICKER}>추천 종목</span>
        <span className="text-[10.5px] text-dc-ink3">(현금 {recs ? recs.cash.toLocaleString('ko-KR') : '─'}원 활용)</span>
      </p>
      <p className="text-dc-ink3 text-[10.5px] mb-3 leading-relaxed">
강하면서 <span className="text-dc-up">지금 살 만함</span>(아직 안 오른) 순 · <span className="text-dc-warn">추격주의</span>=이미 많이 올라 추격 위험 · 6개월·오늘 등락으로 과열 표시 · <span className="text-dc-ok">분산 큼</span>=내 보유와 안 겹침. 한 종목 몰빵 말고 분할 매수.
      </p>
      {loadingRecs ? (
        <p className="flex items-center gap-2 text-dc-ink2 text-[13px] py-2">
          <Loader2 size={14} className="animate-spin" /> 후보 분석 중...
        </p>
      ) : recs && (() => {
        const isKr = recMarket === 'kr';
        const trendF = recs.trend.filter((c) => !!c.is_kr === isKr);
        const revF = recs.reversion.filter((c) => !!c.is_kr === isKr);
        return (
        <div className="space-y-3">
          {/* 한국/미국 토글 — 시장별 추천 (섞으면 미국주가 다 차지) */}
          <div className="flex gap-1.5">
            {([['kr', '🇰🇷 한국'], ['us', '🇺🇸 미국']] as const).map(([m, label]) => (
              <button
                key={m}
                onClick={() => setRecMarket(m)}
                className={recMarket === m ? ON : GHOST}
              >
                {label}
              </button>
            ))}
          </div>
          {trendF.length > 0 && (
            <div>
              <p className={`${KICKER} mb-2`}>📈 추세 리더 <span className="text-dc-ink3/70">(진입 타이밍 순)</span></p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {trendF.map((c) => <RecCard key={c.ticker} c={c} onClick={onSelectTicker} />)}
              </div>
            </div>
          )}
          {revF.length > 0 && (
            <div>
              <p className={`${KICKER} mb-2`}>🔄 역추세 반등 <span className="text-dc-ink3/70">(과열장 — 눌림목)</span></p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {revF.map((c) => <RecCard key={c.ticker} c={c} onClick={onSelectTicker} />)}
              </div>
            </div>
          )}
          {trendF.length === 0 && revF.length === 0 && (
            <p className="text-dc-ink3 text-[11.5px] py-1">{isKr ? '한국' : '미국'} 추천 후보가 없습니다.</p>
          )}
        </div>
        );
      })()}
    </div>
  );
  const hasTrades = (trades?.summary.count ?? 0) > 0;
  const realizedBlock = hasTrades ? <RealizedTrades trades={trades!} unrealizedPnl={s.total_pnl} /> : null;

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-6xl mx-auto p-4 space-y-4">

        <div className="-m-4 mb-0">
          <GuideCard id="mystatus" title="내 현황 사용법">
            <p className="mb-1.5"><b className="text-dc-ink1">이 화면은?</b> 내 보유 종목 <b>손익·비중</b> + 오늘 <b>시장 분석</b>을 한눈에.</p>
            <p><b className="text-dc-ink2">이렇게 쓰세요:</b> 관심종목 탭에서 보유를 입력하면 여기 <b>총손익·종목별 비중·시장 노출</b>이 뜨고, <b>AI 브리핑</b>이 내 포지션 관점으로 코멘트해요. 아래 <b>시장 분석</b>은 지금 들어갈/뺄 타이밍의 참고 지표(<b>공포·탐욕 지수</b>·시나리오 확률·진입 신호판)입니다. 공포·탐욕은 남들과 반대로 보는 눈이에요 — <b>극공포면 매수 기회, 극탐욕이면 과열 경계</b>(한국 자체 지수 + CNN 미국).</p>
          </GuideCard>
        </div>

        {/* ===== 상단 요약 — 한눈 숫자 + 시장 한 줄 (항상 맨 위) ===== */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-dc-ink1 text-[14px] font-[750]">📊 내 현황</h2>
            <span className={`${CHIP} ${TONE.mute}`}>{hasPositions ? '보유 중' : '현금 대기'}</span>
            <div className="ml-auto flex items-center gap-2">
              {confirmReset ? (
                <span className="flex items-center gap-1.5 text-[11.5px]">
                  <span className="text-dc-up hidden sm:inline">보유·현금·매매 초기화?</span>
                  <span className="text-dc-up sm:hidden">초기화?</span>
                  <button onClick={doReset} className={DANGER_ON}>예</button>
                  <button onClick={() => setConfirmReset(false)} className={GHOST}>취소</button>
                </span>
              ) : (
                <button
                  onClick={() => setConfirmReset(true)}
                  className={DANGER}
                  title="보유·현금·매매기록 초기화 (관심종목 목록은 유지)"
                >
                  <Trash2 size={12} /> 초기화
                </button>
              )}
              <button
                onClick={load}
                disabled={loading}
                className={GHOST}
              >
                <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> 새로고침
              </button>
            </div>
          </div>

          {/* 한눈 숫자 (KPI) — 카드 상자 대신 선으로 나눈 구획 */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 sm:gap-x-8 gap-y-5">
            <StatCell label="총자산" value={won(s.total_account)} unit="원" sub="주식 + 현금" hero />
            <StatCell label={`주식 평가액 (${s.count}종목)`} value={won(s.total_eval)} unit="원" sub={`원금 ${won(s.total_cost)}`} divider={KPI_DIV} />
            <CashCell cash={s.cash} cashPct={s.cash_pct} onSave={saveCash} divider={KPI_DIV_LG} />
            <StatCell
              label="총 손익"
              value={`${s.total_pnl_pct >= 0 ? '+' : ''}${s.total_pnl_pct}`}
              unit="%"
              sub={`${s.total_pnl >= 0 ? '+' : ''}${won(s.total_pnl)}원`}
              color={pnlColor}
              divider={KPI_DIV}
            />
          </div>

          {/* 시장 한 줄 (시장국면 교차 — 여기 한 곳에만, 시장 분석 카드와 중복 제거) */}
          <div className="border-b border-dc-hair pb-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11.5px]">
              <span className="text-dc-ink3 flex items-center gap-1"><TrendingUp size={12} /> 시장</span>
              <span className="text-dc-ink2">종합 <b className={r.overall === '양호' ? 'text-dc-ok' : 'text-dc-point'}>{r.overall}</b></span>
              <span className={overheat ? 'text-dc-warn' : 'text-dc-ink2'}>코스피 200일선 대비 <b className="tabular-nums">{r.kospi_above_ma200_pct}%</b>{overheat && ' ⚠️과열'}</span>
              <span className={(r.distribution_days ?? 0) >= 5 ? 'text-dc-warn' : 'text-dc-ink2'}>분산일 <b className="tabular-nums">{r.distribution_days}</b>일</span>
              <span className="text-dc-ink2">VIX <b className="tabular-nums">{r.vix}</b></span>
            </div>
            {r.message && <p className="text-dc-ink3 text-[11.5px] mt-1.5">{r.message}</p>}
          </div>
        </div>

        {/* 🔔 알림 — 점검 필요 보유 종목 (손절선·시그널 약화·급락) */}
        {data!.alerts && data!.alerts.length > 0 && (
          <div className="rounded-lg border border-dc-warn/25 bg-dc-warn/[0.04] px-3.5 py-3">
            <p className="text-dc-warn text-[11.5px] font-semibold mb-1.5">🔔 점검 필요 — 보유 {data!.alerts.length}건</p>
            <ul className="space-y-1">
              {data!.alerts.map((a) => (
                <li key={a.ticker} className="text-[11.5px] flex flex-wrap items-baseline gap-x-1.5">
                  <span className="text-dc-ink1 font-medium">{a.name}</span>
                  <span className="text-dc-ink2">{a.reasons.join(' · ')}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 📈 자산 추이 (하루 1개 스냅샷; 며칠 쌓이면 그래프) */}
        {data!.equity && data!.equity.length > 0 && (
          <div className="border-b border-dc-hair pb-4">
            <p className={`${KICKER} mb-2`}>📈 자산 추이 <span className="text-dc-ink3/70">(하루 1개 기록 · 총자산)</span></p>
            {data!.equity.length >= 2 ? (
              <EquitySparkline data={data!.equity} />
            ) : (
              <p className="text-dc-ink3 text-[11.5px]">오늘부터 기록 시작 — 며칠 쌓이면 그래프로 보여드려요.</p>
            )}
          </div>
        )}

        {/* ===== 본문 2단 — 가로 공간 활용 (좁은 화면은 자동 세로 1단) ===== */}
        <div className="flex flex-col lg:flex-row gap-4 items-start">
          {/* 메인 — 보유 포트폴리오(시장 맥락은 스트립 한 줄, 자세히는 시장 탭) */}
          <div className="w-full lg:flex-1 min-w-0 space-y-4">
            <MarketStrip onTab={onTab} />
            {hasPositions && (
              <>
            {/* AI 브리핑 (보유 시에만) */}
            <div className="rounded-lg border border-dc-point/25 bg-dc-point/[0.04] px-3.5 py-3.5">
              <p className="flex items-center gap-1.5 mb-2.5">
                <Sparkles size={13} className="text-dc-point" />
                <span className={KICKER}>AI 브리핑</span>
              </p>
              {loadingBrief ? (
                <div className="space-y-2 animate-pulse">
                  <div className="h-4 w-2/3 bg-dc-bg2 rounded" />
                  <div className="h-3 w-full bg-dc-bg2/60 rounded" />
                  <div className="h-3 w-11/12 bg-dc-bg2/60 rounded" />
                  <div className="h-3 w-5/6 bg-dc-bg2/60 rounded" />
                  <p className="flex items-center gap-1.5 text-dc-ink3 text-[11.5px] pt-1">
                    <Loader2 size={12} className="animate-spin" /> AI 브리핑 작성 중…
                  </p>
                </div>
              ) : brief ? (
                <div className="space-y-3">
                  {brief.headline && <p className="text-dc-ink1 text-[14px] font-[750]">{brief.headline}</p>}
                  {brief.diagnosis && (
                    <div>
                      <p className={`${KICKER} mb-1`}>진단</p>
                      <p className="text-dc-ink2 text-[13px] leading-relaxed">{brief.diagnosis}</p>
                    </div>
                  )}
                  {brief.market_position && (
                    <div>
                      <p className={`${KICKER} mb-1`}>시장 속 내 위치</p>
                      <p className="text-dc-ink2 text-[13px] leading-relaxed">{brief.market_position}</p>
                    </div>
                  )}
                  {brief.positions_note?.length > 0 && (
                    <div>
                      <p className={`${KICKER} mb-1.5`}>종목별 점검</p>
                      <ul className="space-y-1">
                        {brief.positions_note.map((n, i) => (
                          <li key={i} className="text-[13px] text-dc-ink2 leading-snug flex gap-1.5">
                            <span className="text-dc-ink1 font-medium shrink-0">{n.name}</span>
                            <span className="text-dc-ink3">·</span>
                            <span>{n.note}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {brief.cash_strategy && (
                    <div>
                      <p className={`${KICKER} mb-1`}>현금 전략</p>
                      <p className="text-dc-ink2 text-[13px] leading-relaxed">{brief.cash_strategy}</p>
                    </div>
                  )}
                  {brief.actions?.length > 0 && (
                    <div className="rounded-lg border border-dc-line1 bg-dc-bg1/60 px-3 py-2.5">
                      <p className="text-[10.5px] font-[650] tracking-[.12em] text-dc-point mb-1.5">✅ 이번 주 할 일</p>
                      <ul className="space-y-1.5">
                        {brief.actions.map((a, i) => (
                          <li key={i} className="text-[13px] text-dc-ink1 leading-snug flex gap-2">
                            <span className="shrink-0 w-4 h-4 rounded border border-dc-point/50 text-dc-point flex items-center justify-center text-[10.5px] mt-px">{i + 1}</span>
                            <span>{a}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* ===== 보유 포트폴리오 (구성·리스크·분류·종목 한 묶음) ===== */}
            <div className="space-y-4">
              <p className="text-dc-ink1 text-[14px] font-[750]">📦 보유 포트폴리오</p>

              {/* 자산 구성 */}
              <div className="border-b border-dc-hair pb-4">
                <AllocationBar invested={s.invested_pct} cash={s.cash_pct} />
                <p className="text-dc-ink3 text-[11.5px] mt-2.5">
                  보유 평균 추세점수 <b className="text-dc-ink2 tabular-nums">{s.avg_trend ?? '─'}</b> · 평균 RS순위 <b className="text-dc-ink2 tabular-nums">{s.avg_rs ?? '─'}</b>
                  {overheat && s.cash_pct < 20 && <span className="text-dc-warn"> · ⚠️ 과열장인데 현금비중 낮음</span>}
                  {overheat && s.cash_pct >= 20 && <span className="text-dc-ok"> · 과열장 — 현금이 방어막</span>}
                </p>
              </div>

              {/* 리스크 점검 (집중도·스트레스·건강) — 구버전 백엔드 대비 가드 */}
              {data!.health && data!.concentration && (
              <div className="border-b border-dc-hair pb-4 space-y-2.5">
                <div className="flex items-center gap-2">
                  <span className={KICKER}>리스크 점검</span>
                  {(() => {
                    const h = data!.health;
                    const cls = h.level === '양호' ? TONE.ok
                      : h.level === '경고' ? TONE.warn
                      : TONE.point;
                    return <span className={`${MINICHIP} ${cls}`}>건강 {h.level}</span>;
                  })()}
                  {data!.health.reasons.length > 0 && (
                    <span className="text-dc-ink3 text-[11.5px]">{data!.health.reasons.join(' · ')}</span>
                  )}
                </div>

                {/* 집중도 */}
                <div className="text-[11.5px]">
                  <span className="text-dc-ink3">집중도 </span>
                  <span className={data!.concentration.level === '매우 높음' || data!.concentration.level === '높음' ? 'text-dc-warn' : 'text-dc-ink2'}>
                    최대 <b>{data!.concentration.top_name}</b> {data!.concentration.top_weight}%
                    {' '}(상위2 {data!.concentration.top2_weight}%) · 쏠림 <b>{data!.concentration.level}</b>
                  </span>
                </div>

                {/* 유형 분해 (주식/ETF/미국 — 한국 개별주는 섹터 데이터가 없어 유형 기준) */}
                {data!.exposure && data!.exposure.length > 0 && (
                  <div className="text-[11.5px]" title="자산 유형별 비중. 한국 개별주는 섹터 분류가 없어 유형(주식/ETF/미국) 기준입니다.">
                    <span className="text-dc-ink3">유형 분해 </span>
                    {data!.exposure.map((e, i) => (
                      <span key={e.group} className="text-dc-ink2">
                        {i > 0 && <span className="text-dc-ink3"> · </span>}
                        {e.group} <b>{e.weight_pct}%</b>
                      </span>
                    ))}
                    {data!.exposure[0].weight_pct >= 80 && data!.summary.count > 1 && (
                      <span className="text-dc-warn"> ⚠️ {data!.exposure[0].group}에 쏠림</span>
                    )}
                  </div>
                )}

                {/* 스트레스 테스트 */}
                <div>
                  <p className="text-dc-ink3 text-[11.5px] mb-1.5">스트레스 테스트 <span className="text-dc-ink3/70">(보유주식 하락 시 계좌 충격, 현금이 완충)</span></p>
                  <div className="flex gap-2">
                    {(data!.stress || []).map((st) => (
                      <div key={st.drop_pct} className="flex-1 rounded-md border border-dc-line1 bg-dc-bg1/60 px-2 py-1.5 text-center">
                        <div className="text-dc-ink3 text-[10.5px]">−{st.drop_pct}%</div>
                        <div className="text-dc-down text-[11.5px] font-semibold tabular-nums">{st.account_pct}%</div>
                        <div className="text-dc-ink3 text-[10.5px]">{won(st.loss)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              )}

              {/* 분류 칩 */}
              {(data!.buckets.overheated.length > 0 || data!.buckets.cut.length > 0 || data!.buckets.core.length > 0) && (
                <div className="border-b border-dc-hair pb-4 space-y-2">
                  <TagRow label="🔥 과열(RSI 70+)" tags={data!.buckets.overheated} tone="text-dc-warn" onClick={onSelectTicker} />
                  <TagRow label="⚠️ 정리후보" tags={data!.buckets.cut} tone="text-dc-down" onClick={onSelectTicker} />
                  <TagRow label="✅ 핵심보유" tags={data!.buckets.core} tone="text-dc-ok" onClick={onSelectTicker} />
                </div>
              )}

              {/* 보유 종목 테이블 */}
              <div>
                <p className={`${KICKER} mb-2`}>보유 종목</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-[13px]">
                    <thead>
                      <tr className="border-b border-dc-hair">
                        <th className="px-2 sm:px-3 py-2.5 text-dc-ink3 text-[10.5px] font-[650] tracking-[.12em] select-none whitespace-nowrap">종목</th>
                        <th className="px-2 sm:px-3 py-2.5 text-dc-ink3 text-[10.5px] font-[650] tracking-[.12em] select-none whitespace-nowrap text-right">비중</th>
                        <th className="px-2 sm:px-3 py-2.5 text-dc-ink3 text-[10.5px] font-[650] tracking-[.12em] select-none whitespace-nowrap text-right">평가액</th>
                        <th className="px-2 sm:px-3 py-2.5 text-dc-ink3 text-[10.5px] font-[650] tracking-[.12em] select-none whitespace-nowrap text-right">손익</th>
                        <th className="px-2 sm:px-3 py-2.5 text-dc-ink3 text-[10.5px] font-[650] tracking-[.12em] select-none whitespace-nowrap">시그널</th>
                        <th className="px-2 sm:px-3 py-2.5 text-dc-ink3 text-[10.5px] font-[650] tracking-[.12em] select-none whitespace-nowrap text-right">RSI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data!.positions.map((p: PortfolioPosition) => {
                        const c = p.pnl_pct > 0 ? 'text-dc-up' : p.pnl_pct < 0 ? 'text-dc-down' : 'text-dc-ink2';
                        return (
                          <tr
                            key={p.ticker}
                            onClick={() => onSelectTicker(p.ticker, p.name)}
                            className="border-b border-dc-hair/80 hover:bg-white/[0.015] transition-colors cursor-pointer"
                          >
                            <td className="px-2 sm:px-3 py-2.5">
                              <div className="text-dc-ink1 text-[13px] font-bold leading-tight">{p.name}</div>
                              <div className="text-dc-ink3 text-[10.5px]">{p.ticker} · {p.market}</div>
                            </td>
                            <td className="px-2 sm:px-3 py-2.5 text-right tabular-nums text-dc-ink2">{p.weight_pct}%</td>
                            <td className="px-2 sm:px-3 py-2.5 text-right tabular-nums text-dc-ink2">{won(p.eval_amount)}</td>
                            <td className={`px-2 sm:px-3 py-2.5 text-right tabular-nums font-bold ${c}`}>
                              {p.pnl_pct >= 0 ? '+' : ''}{p.pnl_pct}%
                            </td>
                            <td className="px-2 sm:px-3 py-2.5">
                              <span className={`${MINICHIP} ${SIGNAL_STYLE[p.signal] ?? TONE.mute}`}>{sigLabel(p.signal)}</span>
                            </td>
                            <td className={`px-2 sm:px-3 py-2.5 text-right tabular-nums ${p.rsi != null && p.rsi >= 70 ? 'text-dc-warn' : 'text-dc-ink2'}`}>
                              {p.rsi ?? '─'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
              </>
            )}
          </div>
          {/* 사이드 — 실현손익 (매매 기록 있을 때만 렌더; 없으면 좌측 시장분석이 풀폭) */}
          {realizedBlock && (
            <div className="w-full lg:w-96 shrink-0 space-y-4">
              {realizedBlock}
            </div>
          )}
        </div>

        {/* ===== 추천 종목 — 전체 폭, 가로 3열로 펼쳐 세로 길이 대폭 감소 ===== */}
        {recsBlock}

        {/* ===== 어드바이저 (맨 아래, 접기 — 펼쳐도 대화 상태 유지) ===== */}
        <div>
          <button
            onClick={() => setAdvisorOpen((o) => !o)}
            className="w-full flex items-center gap-1.5 px-3 py-2 rounded-lg border border-dc-line1 bg-dc-bg1/60 hover:border-dc-line2 transition-colors"
          >
            <MessageCircle size={13} className="text-dc-point" />
            <span className={KICKER}>어드바이저에게 물어보기</span>
            <span className="text-[10.5px] text-dc-ink3">(내 데이터 기준)</span>
            <span className="ml-auto text-dc-point text-[11.5px]">{advisorOpen ? '▾ 접기' : '▸ 펼치기'}</span>
          </button>
          <div className={advisorOpen ? 'mt-2' : 'hidden'}>
            <AdvisorChat />
          </div>
        </div>

        <p className="text-dc-ink3/80 text-[10.5px] flex items-start gap-1 pt-1">
          <AlertTriangle size={11} className="shrink-0 mt-0.5" />
          수치는 보유정보(평단가·수량) + 스캔 데이터 기반 계산값입니다. 추천은 점수가 선별한 후보일 뿐 매수 명령이 아니며, AI 브리핑·추천 모두 참고용이고 투자 책임은 본인에게 있습니다.
        </p>
      </div>
    </div>
  );
}
